import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", mobile: user?.mobile || "", company_name: user?.company_name || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      toast.success("Profile updated");
      await refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/me/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSavingPw(false); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Account</div>
          <h1 className="font-heading text-2xl md:text-4xl">Profile & Security</h1>
          <p className="text-sm text-[#5C6653] mt-2">Update your personal details and change your password.</p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Profile */}
          <form onSubmit={saveProfile} className="border border-[#E5E2DC] bg-white p-6" data-testid="profile-form">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-9 w-9 bg-[#F0EFEA] flex items-center justify-center">
                <UserCog className="h-5 w-5 text-[#234B36]" />
              </div>
              <div>
                <h2 className="font-heading text-xl">Personal Details</h2>
                <div className="text-xs text-[#5C6653]">Email <span className="font-mono-anu text-[#1A1F16]">{user?.email}</span> — cannot be changed here.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Full name</Label>
                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" className="rounded-sm" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Mobile</Label>
                <Input value={profile.mobile} onChange={(e) => setProfile({ ...profile, mobile: e.target.value })} data-testid="profile-mobile" className="rounded-sm" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs mb-1 block">Company (optional)</Label>
                <Input value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} data-testid="profile-company" className="rounded-sm" />
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
                {savingProfile ? "Saving…" : "Save profile"}
              </Button>
            </div>
          </form>

          {/* Password */}
          <form onSubmit={savePassword} className="border border-[#E5E2DC] bg-white p-6" data-testid="password-form">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-9 w-9 bg-[#F0EFEA] flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-[#234B36]" />
              </div>
              <div>
                <h2 className="font-heading text-xl">Change Password</h2>
                <div className="text-xs text-[#5C6653]">Enter your current password, then choose a new one.</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Current password</Label>
                <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password" className="rounded-sm" required />
              </div>
              <div>
                <Label className="text-xs mb-1 block">New password</Label>
                <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password" className="rounded-sm" required minLength={6} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Confirm new</Label>
                <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="confirm-password" className="rounded-sm" required minLength={6} />
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button type="submit" disabled={savingPw} data-testid="save-password-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white">
                {savingPw ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
